import { ME_TOKEN, type FilterGroup } from './filter';
import type { FlowRole } from './role';
import { CONTACT_PLAY_CANDIDATES_STATE_KEY, CONTACT_PLAY_SECTION_STATE_KEYS } from './play-people';
import { isSavedViewId } from './view-state';

export type MeChipEntity = 'account' | 'contact';

/**
 * Each role's own-book `{me}` chip: the group id, its one condition's id, and
 * the ownership field per table. Contact chips for claydr/gtme filter on the
 * ACCOUNT-level field (account-level ownership, 2026-10-16); gs uses the same
 * field on both tables.
 */
export const ROLE_ME_CHIP: Record<FlowRole, { groupId: string; conditionId: string; field: Record<MeChipEntity, string> }> = {
  claydr: { groupId: 'chip-my-claydr', conditionId: 'chip-my-claydr-1', field: { account: 'claydr_name', contact: 'account_claydr_name' } },
  gtme: { groupId: 'chip-my-owner', conditionId: 'chip-my-owner-1', field: { account: 'owner_name', contact: 'account_owner_name' } },
  gs: { groupId: 'chip-my-gs', conditionId: 'chip-my-gs-1', field: { account: 'gs_name', contact: 'gs_name' } },
};

/**
 * The `{me}` chip group for a role. Group and condition ids are the same on
 * both tables (stable: `withRoleDefaults` dedupes by group id); the field
 * comes from `ROLE_ME_CHIP[role].field[entity]`.
 */
export function meChipGroup(role: FlowRole, entity: MeChipEntity): FilterGroup {
  const { groupId, conditionId, field } = ROLE_ME_CHIP[role];
  return {
    id: groupId,
    logic: 'AND',
    conditions: [{ id: conditionId, field: field[entity], operator: 'equals', value: ME_TOKEN }],
  };
}

/** Working-state entities that read the CONTACT filter catalog (identity for `account`, `signal`, `plays`). */
export const CONTACT_ME_CHIP_ENTITIES: ReadonlySet<string> = new Set([
  'contact',
  'contact_account',
  'contact_play',
  CONTACT_PLAY_CANDIDATES_STATE_KEY,
  ...Object.values(CONTACT_PLAY_SECTION_STATE_KEYS),
]);

/**
 * The retired Plays "scope to me" group (`applyPlayScope`, V1 Plays tab
 * 2026-09-13 -> 2026-09-25, producer deleted 2026-10-16). Rows persisted under
 * `contact_play` between 2026-09-23 and 2026-09-25 can still carry it with
 * contact-level fields (the earlier 09-22 shape named the retired
 * `action_completed_by_name` and is dropped whole by `sanitizePlayScopeFilters`).
 */
const LEGACY_PLAY_SCOPE_MINE_GROUP_ID = 'chip-play-scope-mine';

type LegacyRewrite = { from: string; to: string };

/**
 * Seeded `{me}` CONDITIONS by id: the contact-level field each was persisted
 * with BEFORE account-level ownership (2026-10-16) and the account-level field
 * it becomes. A LITERAL table on purpose: a migration describes frozen history,
 * so it must not be derived from live config (`ROLE_ME_CHIP`,
 * `buildPlayMemberMineGroup`). If a seeder moves again, this table stays put
 * and a NEW entry is added. `role-me-chip.test.ts` pins each `to` against the
 * live seeder. Exported for that pin test only.
 */
export const LEGACY_SEEDED_ME_CONDITIONS: ReadonlyMap<string, LegacyRewrite> = new Map<string, LegacyRewrite>([
  ['chip-my-owner-1', { from: 'owner_name', to: 'account_owner_name' }],
  ['chip-my-claydr-1', { from: 'claydr_name', to: 'account_claydr_name' }],
  // buildPlayMemberMineGroup conditions 1-2 only (3 gs_name, 4 play_completed_by never moved).
  ['chip-play-member-mine-1', { from: 'owner_name', to: 'account_owner_name' }],
  ['chip-play-member-mine-2', { from: 'claydr_name', to: 'account_claydr_name' }],
  // Retired scope-mine group, conditions 2-3 only.
  ['chip-play-scope-mine-2', { from: 'claydr_name', to: 'account_claydr_name' }],
  ['chip-play-scope-mine-3', { from: 'owner_name', to: 'account_owner_name' }],
]);

/** Group ids that hold seeded `{me}` conditions (cheap early-out). */
const SEEDED_ME_GROUP_IDS: ReadonlySet<string> = new Set([
  'chip-my-owner',
  'chip-my-claydr',
  'chip-play-member-mine',
  LEGACY_PLAY_SCOPE_MINE_GROUP_ID,
]);

/**
 * Rewrites the SEEDED `{me}` chips in a persisted contact-table working state
 * (`flow_view_state.working_state`) from the contact's own `owner_name` /
 * `claydr_name` to the account-level `account_owner_name` /
 * `account_claydr_name` (account-level ownership, 2026-10-16). Runs on view
 * state GET and PUT, so it must be cheap, pure and idempotent.
 *
 * - **Ids stay stable.** Group and condition ids are never changed:
 *   `withRoleDefaults` dedupes seeds by group id, so an already-stored chip
 *   keeps suppressing the fresh seed (no duplicate narrowing chip) and the
 *   failure mode of a missed path is "old behaviour persists", never a double
 *   filter.
 * - **Only seeded conditions, only `{me}`.** A condition is rewritten only when
 *   its id is a seeded condition id, its field is the legacy field it was
 *   seeded with, AND its value is `ME_TOKEN`. A rep-added condition inside a
 *   seeded group (FilterPopover ids are `<prefix>_<n>_<ts>`, so never collide),
 *   a seeded condition the rep re-pointed, a rep's "Owner is Jane", or any
 *   hand-built group is a real edit and is left alone. The operator is
 *   deliberately not gated on.
 * - **Saved views are never touched** — neither in `flow_views` nor in working
 *   state. Working state whose `activeViewId` is a numeric saved-view id is
 *   returned unchanged, because the client applies it over the saved
 *   definition (rewriting would make the view read "Modified" and narrow
 *   results). Only default working state (`activeViewId` null or a preset
 *   slug) migrates. `undefined` = pointer unknown (key omitted from a PUT
 *   body): skipped; GET migrates on the next read.
 *
 * Returns the SAME reference when nothing changed (same contract as
 * `migrateLegacyViewFieldKeys`); on a rewrite, every untouched group and
 * condition (and every non-`filter` key) is kept by reference.
 */
export function migrateLegacyContactMeChips<S extends { filter: FilterGroup[] }>(
  entity: string,
  state: S,
  activeViewId: string | null | undefined,
): S {
  if (!CONTACT_ME_CHIP_ENTITIES.has(entity)) return state;
  if (activeViewId === undefined || isSavedViewId(activeViewId)) return state;

  let changed = false;
  const nextFilter = state.filter.map((group) => {
    if (!SEEDED_ME_GROUP_IDS.has(group.id)) return group;
    let groupChanged = false;
    const nextConditions = group.conditions.map((cond) => {
      const legacy = LEGACY_SEEDED_ME_CONDITIONS.get(cond.id);
      if (!legacy || cond.field !== legacy.from || cond.value !== ME_TOKEN) return cond;
      groupChanged = true;
      return { ...cond, field: legacy.to };
    });
    if (!groupChanged) return group;
    changed = true;
    return { ...group, conditions: nextConditions };
  });

  return changed ? { ...state, filter: nextFilter } : state;
}
